import { isBrowser, globalVar } from 'app/infrastructure/universal';
import { AppSetting } from 'dependencies/appsettings';
import { SigninRequestSettings, UserState } from './types';
import { logoutRoute } from './constants';
import { UserManager, UserManagerSettings } from 'oidc-client-ts';
import { debug } from 'app/infrastructure/log';

let userManager: UserManager;

// prevent paralell requests triggering multiple redirects to auth provider
let redirectStarted: boolean;
export const isRedirectStarted = () => redirectStarted;

const logoutPath = (applicationUrl: string) => {
  return (applicationUrl.lastIndexOf('/') === applicationUrl.length - 1
    ? applicationUrl.slice(0, -1) : applicationUrl)
    + logoutRoute;
};

function createAuthCodeSettings(): UserManagerSettings {
  return {
    authority: AppSetting.OidcServiceUrl,
    client_id: AppSetting.OidcClientId,
    client_secret: undefined,
    redirect_uri: AppSetting.ApplicationUrl,
    post_logout_redirect_uri: logoutPath(AppSetting.ApplicationUrl),
    scope: AppSetting.OidcScope,
    monitorSession: false,
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    loadUserInfo: !AppSetting.OidcDontLoadUserInfo
  };
}

function createUserManager(): UserManager {
  const settings = createAuthCodeSettings();
  return new UserManager(settings);
}

async function accessExpiringCallback() {
  debug('Access token expiring');
  if (!await renewToken()) {
    debug('Failed silent sign in. Redirecting...');
    reAuthenticate();
  }
}

export function initialize() {
  if (!isBrowser || userManager) {
    return;
  }
  userManager = createUserManager();
  userManager.clearStaleState();
  userManager.events.addAccessTokenExpiring(accessExpiringCallback);
}

export function removeUserManagerEvents() {
  userManager?.events.removeAccessTokenExpiring(accessExpiringCallback);
}

export const routerPath = (href: string, hostingPath: string) => {
  if (href === undefined || href === null) {
    return '/';
  }

  const hostPosition = href.indexOf('://') !== -1 ? href.indexOf('://') + 3 : 0;
  const locationPosition = href.indexOf('/', hostPosition);
  const locationPath = locationPosition !== -1 ? href.substring(locationPosition) : '/';

  if (hostingPath === undefined || hostingPath === null || hostingPath.length === 0) {
    return locationPath;
  }

  const hostingPathPosition = locationPath.toLowerCase().indexOf(hostingPath.toLowerCase());
  const hostingPathLength = hostingPath.length - (hostingPath.lastIndexOf('/') === hostingPath.length - 1 ? 1 : 0);
  return hostingPathPosition === -1 || locationPath.length === hostingPathPosition + hostingPathLength
    ? '/'
    : locationPath.substring(hostingPathPosition + hostingPathLength);
};

export function reAuthenticate() {
  if (redirectStarted) {
    return;
  }
  redirectStarted = true;
  signin(true, true);
}

async function renewToken(): Promise<boolean> {
  try {
    debug('Silent sign in');
    await userManager.signinSilent();
    return true;
  } catch (err) {
    return false;
  }
}

export async function signin(logAsDifferentUser: boolean = false, storeCurrentRouterPath: boolean = true) {
  initialize();

  const path = storeCurrentRouterPath
    ? routerPath(globalVar('location').href, AppSetting.HostingPath)
    : '/';

  const settings: SigninRequestSettings = {
    state: { redirectUrl: path }
  };

  const providerId = AppSetting.OidcDefaultProviderId;

  if (logAsDifferentUser) {
    settings.prompt = 'login';
  } else if (providerId) {
    settings.acr_values = 'idp:' + providerId;
  }

  await userManager.signinRedirect(settings);
}

export async function processSigninResponse(url: string) {
  initialize();
  const user = await userManager.signinRedirectCallback(url);
  return (user?.state as UserState)?.redirectUrl;
}

export async function signout() {
  initialize();
  userManager.signoutRedirect();
  userManager.removeUser();
}

export async function getAccessToken() {
  initialize();
  const user = await userManager?.getUser();
  return user?.access_token;
}

export async function hasToken() {
  return !!getAccessToken();
}

